/**
 * B2BOrderDetail — Détail d'un dossier B2B.
 *
 * Extrait de l'ex-`B2BTab.jsx` (Phase A++) sans changement de comportement.
 * Orchestre l'ensemble des interactions admin sur un dossier :
 *   - Vue ClientFeedbackCard (Phase A++)
 *   - Barre d'actions rapides (Préparer nouvelle cotation, Contacter, Marquer
 *     à relancer, Annuler)
 *   - Métadonnées (clinique, dates, montants, échéances) éditables
 *   - Pièce jointe demande client
 *   - Cotation PDF + workflow Médiciel + Bons de livraison
 *   - Table des lignes avec décoration ligne-par-ligne (line_actions Phase A++)
 *   - Changement statut/paiement + Observations + Historique
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Wallet, AlertCircle, History, Trash2, Save, Eye, Loader2,
  CheckCircle2, XCircle, Clock, Package, Truck,
  FileText, Image as ImageIcon, Paperclip, RefreshCw,
  FileEdit, Bell, ListChecks, PhoneCall, Ban,
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import {
  CotationUploadCard, SendCotationModal, DeleteConfirmModal,
} from './B2BAdvancedModals';
import { B2BDeliveryNotesSection } from './B2BDeliveryNotes';
import B2BDocumentsSection from './B2BDocumentsSection';
import { B2BPdfWorkflowSection } from './B2BPdfWorkflow';
import { CotationVersionsTimeline } from './B2BCotationVersionsTimeline';
import {
  API, auth, CHANNEL_LABELS, MODE_LABELS, StatusBadge, PaymentBadge,
  LINE_ACTION_LABELS, LINE_ACTION_TONES, fmtDateTime,
} from './B2BConstants';
import { DeliveryDelayCell } from './B2BDeliveryDelayCell';
import { ClientFeedbackCard } from './B2BClientFeedback';

// Phase A++ : helper pour transformer un payload d'erreur Axios en string,
// même quand le backend renvoie un array Pydantic [{msg, loc, ...}]. Sans
// cela, `toast.error()` reçoit un objet → React crash dans <Toaster>
// (« Objects are not valid as a React child »).
const errMsg = (e, fallback = 'Erreur') => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d)))
      .join(' · ');
  }
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
};

export const OrderDetail = ({ orderId, meta, canWrite, canDelete, clinics, onBack, onChanged, onDeleted }) => {
  const [order, setOrder] = useState(null);
  const [history, setHistory] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [newPay, setNewPay] = useState('');
  const [payComment, setPayComment] = useState('');
  const [editingDeadline, setEditingDeadline] = useState(false);
  const [deadlineDraft, setDeadlineDraft] = useState('');
  const [editingAmount, setEditingAmount] = useState(false);
  const [amountDraft, setAmountDraft] = useState('');
  const [editingPayDue, setEditingPayDue] = useState(false);
  const [payDueDraft, setPayDueDraft] = useState('');
  const [commentDraft, setCommentDraft] = useState('');
  const [commentTag, setCommentTag] = useState('autre');
  const [saving, setSaving] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [o, h, c] = await Promise.all([
        axios.get(`${API}/b2b/orders/${orderId}`, { headers: auth() }),
        axios.get(`${API}/b2b/orders/${orderId}/history`, { headers: auth() }),
        axios.get(`${API}/b2b/orders/${orderId}/comments`, { headers: auth() }),
      ]);
      setOrder(o.data);
      setHistory(h.data);
      setComments(c.data);
      setNewStatus(o.data.status);
      setNewPay(o.data.status_payment);
      setDeadlineDraft(o.data.delivery_deadline || '');
      setAmountDraft(o.data.total_amount != null ? String(o.data.total_amount) : '');
      setPayDueDraft(o.data.payment_due_date || '');
    } catch (e) {
      toast.error(errMsg(e, 'Erreur de chargement'));
    }
    setLoading(false);
  }, [orderId]);

  useEffect(() => { reload(); }, [reload]);

  const applyStatus = async () => {
    if (!newStatus || newStatus === order.status) { toast.info('Statut inchangé'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/b2b/orders/${orderId}/status`, { status: newStatus, comment: statusComment || null }, { headers: auth() });
      toast.success('Statut mis à jour');
      setStatusComment('');
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  const applyPay = async () => {
    if (!newPay || newPay === order.status_payment) { toast.info('Paiement inchangé'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/b2b/orders/${orderId}/payment`, { status_payment: newPay, comment: payComment || null }, { headers: auth() });
      toast.success('Paiement mis à jour');
      setPayComment('');
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  const applyDeadline = async () => {
    if (!deadlineDraft || deadlineDraft === order.delivery_deadline) { setEditingDeadline(false); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/b2b/orders/${orderId}`, { delivery_deadline: deadlineDraft }, { headers: auth() });
      toast.success('Délai mis à jour');
      setEditingDeadline(false);
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  const applyAmount = async () => {
    const numeric = amountDraft === '' ? null : parseFloat(amountDraft);
    if (numeric === order.total_amount) { setEditingAmount(false); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/b2b/orders/${orderId}`, { total_amount: numeric }, { headers: auth() });
      toast.success('Montant mis à jour');
      setEditingAmount(false);
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  const applyPayDue = async () => {
    if (payDueDraft === (order.payment_due_date || '')) { setEditingPayDue(false); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/b2b/orders/${orderId}`, { payment_due_date: payDueDraft || null }, { headers: auth() });
      toast.success('Échéance mise à jour');
      setEditingPayDue(false);
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  const addComment = async () => {
    const body = (commentDraft || '').trim();
    if (!body) { toast.error('Saisissez une observation'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/b2b/orders/${orderId}/comments`, { body, tag: commentTag }, { headers: auth() });
      toast.success('Observation ajoutée');
      setCommentDraft('');
      setCommentTag('autre');
      await reload();
      onChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const text = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map((d) => d.msg).join(' · ')
        : 'Erreur lors de l\'ajout';
      toast.error(text);
    } finally { setSaving(false); }
  };

  const doDelete = async (reason) => {
    try {
      await axios.delete(`${API}/b2b/orders/${orderId}`, { headers: auth(), data: { reason } });
      toast.success('Dossier envoyé en corbeille');
      onDeleted?.();
    } catch (e) {
      toast.error(errMsg(e, 'Erreur suppression'));
      throw e;
    }
  };

  const uploadAttachment = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 8 Mo)'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/b2b/orders/${orderId}/attachment`, fd, {
        headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Demande client jointe au dossier');
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, 'Erreur upload'));
    } finally { setSaving(false); }
  };

  // ─── Phase A++ : actions admin sur retour client ─────────────────────────
  const setStatusInline = async (statusId, msg, confirm) => {
    if (confirm && !window.confirm(confirm)) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/b2b/orders/${orderId}/status`,
        { status: statusId, comment: msg || null },
        { headers: auth() },
      );
      toast.success('Statut mis à jour');
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  const markForRelance = async () => {
    const note = window.prompt('Note pour le suivi (optionnel) :', 'À relancer dans quelques jours.');
    if (note === null) return; // annulé
    setSaving(true);
    try {
      await axios.post(
        `${API}/b2b/orders/${orderId}/mark-relance`,
        { note: note || null },
        { headers: auth() },
      );
      toast.success('Dossier marqué « à relancer »');
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  const contactClient = () => {
    const phone = (order?.phone || '').replace(/\D/g, '');
    if (!phone) { toast.error('Aucun téléphone enregistré pour ce dossier.'); return; }
    const intl = phone.length === 10 ? `225${phone}` : phone;
    const requester = order?.requester_name || order?.clinic_name || 'Bonjour';
    const text = encodeURIComponent(
      `Bonjour ${requester}, nous revenons vers vous concernant votre demande de cotation. `
      + `Pouvons-nous échanger pour la suite ?`,
    );
    window.open(`https://wa.me/${intl}?text=${text}`, '_blank', 'noopener');
  };

  if (loading || !order) {
    return <div className="p-6 text-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  const statusLabel = meta?.statuses?.find((s) => s.id === order.status)?.label || order.status;
  const payLabel = meta?.payment_statuses?.find((s) => s.id === order.status_payment)?.label || order.status_payment;

  // Phase A++ : index des actions ligne-par-ligne (modif client) pour affichage inline.
  const lineActionByIndex = {};
  (order.cotation_modification_request?.line_actions || []).forEach((la) => {
    if (Number.isInteger(la?.item_index)) lineActionByIndex[la.item_index] = la;
  });

  return (
    <div className="space-y-4" data-testid="b2b-order-detail">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="b2b-detail-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour à la liste
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => { await reload(); onChanged?.(); toast.success('Dossier actualisé'); }}
            disabled={loading}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="b2b-detail-refresh"
            title="Recharger le dossier pour voir les signatures et statuts à jour"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge id={order.status} label={statusLabel} />
          <PaymentBadge id={order.status_payment} label={payLabel} />
          {canDelete && (
            <Button size="sm" variant="outline" onClick={() => setShowDelete(true)} className="border-red-200 text-red-700 hover:bg-red-50" data-testid="b2b-order-delete">
              <Trash2 className="w-4 h-4 mr-1" /> Supprimer
            </Button>
          )}
        </div>
      </div>

      {/* Phase A++ : retour structuré du client (révision/refus) */}
      <ClientFeedbackCard order={order} />

      {/* Phase A++ : actions admin rapides lorsqu'un retour client est présent */}
      {canWrite && ['modification_requested', 'cotation_rejected', 'cotation_refused_final', 'a_relancer'].includes(order.status) && (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="b2b-feedback-actions">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-zinc-700 mb-3 flex items-center gap-1.5">
              <ListChecks className="w-4 h-4 text-amber-700" /> Actions à mener
            </h3>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => setStatusInline('nouvelle_cotation_a_preparer', 'Nouvelle cotation à préparer suite au retour client.')}
                disabled={saving || order.status === 'nouvelle_cotation_a_preparer'}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="b2b-action-prepare-new-cotation"
              >
                <FileEdit className="w-4 h-4 mr-1" /> Préparer nouvelle cotation
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={contactClient}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="b2b-action-contact"
              >
                <PhoneCall className="w-4 h-4 mr-1" /> Contacter (WhatsApp)
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={markForRelance}
                disabled={saving || order.status === 'a_relancer'}
                className="border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
                data-testid="b2b-action-mark-relance"
              >
                <Bell className="w-4 h-4 mr-1" /> Marquer à relancer
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setStatusInline(
                  'annulee',
                  'Dossier annulé suite au retour client.',
                  'Annuler définitivement ce dossier ? Cette action peut être revue ultérieurement via le sélecteur de statut.',
                )}
                disabled={saving || order.status === 'annulee'}
                className="border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                data-testid="b2b-action-cancel"
              >
                <Ban className="w-4 h-4 mr-1" /> Annuler
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-zinc-200">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div><p className="text-xs text-zinc-500 uppercase">Clinique</p><p className="font-semibold">{order.clinic_name}</p></div>
            <div><p className="text-xs text-zinc-500 uppercase">Demandeur</p><p>{order.requester_name || '—'}</p></div>
            <div><p className="text-xs text-zinc-500 uppercase">Téléphone</p><p>{order.phone || '—'}</p></div>
            <div><p className="text-xs text-zinc-500 uppercase">Canal</p><p>{CHANNEL_LABELS[order.channel] || order.channel}</p></div>
            <div><p className="text-xs text-zinc-500 uppercase">Mode</p><p>{MODE_LABELS[order.mode] || order.mode}</p></div>
            <div data-testid="b2b-deadline-block">
              <p className="text-xs text-zinc-500 uppercase">Délai prévu</p>
              {editingDeadline && canWrite ? (
                <div className="flex items-center gap-1 mt-0.5">
                  <Input type="date" value={deadlineDraft} onChange={(e) => setDeadlineDraft(e.target.value)} className="h-8 text-sm" data-testid="b2b-deadline-input" />
                  <Button size="icon" variant="ghost" onClick={applyDeadline} disabled={saving} data-testid="b2b-deadline-save"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => { setEditingDeadline(false); setDeadlineDraft(order.delivery_deadline || ''); }}><XCircle className="w-4 h-4 text-zinc-400" /></Button>
                </div>
              ) : (
                <div className="flex items-center gap-1 mt-0.5">
                  <p className={order.delivery_deadline ? '' : 'text-zinc-400 italic'}>{order.delivery_deadline || 'Non défini'}</p>
                  {canWrite && (
                    <Button size="icon" variant="ghost" onClick={() => setEditingDeadline(true)} title="Modifier le délai" data-testid="b2b-deadline-edit">
                      <Clock className="w-3.5 h-3.5 text-zinc-400" />
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div><p className="text-xs text-zinc-500 uppercase">Créé</p><p>{fmtDateTime(order.created_at)}</p></div>
            <div><p className="text-xs text-zinc-500 uppercase">Modifié</p><p>{fmtDateTime(order.updated_at)}</p></div>
            <div><p className="text-xs text-zinc-500 uppercase">Créé par</p><p>{order.created_by_name || '—'}</p></div>
            <div data-testid="b2b-amount-block">
              <p className="text-xs text-zinc-500 uppercase">Montant HT (FCFA)</p>
              {editingAmount && canWrite ? (
                <div className="flex items-center gap-1 mt-0.5">
                  <Input type="number" min="0" step="100" value={amountDraft} onChange={(e) => setAmountDraft(e.target.value)} className="h-8 text-sm w-32" data-testid="b2b-amount-input" />
                  <Button size="icon" variant="ghost" onClick={applyAmount} disabled={saving} data-testid="b2b-amount-save"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => { setEditingAmount(false); setAmountDraft(order.total_amount != null ? String(order.total_amount) : ''); }}><XCircle className="w-4 h-4 text-zinc-400" /></Button>
                </div>
              ) : (
                <div className="flex items-center gap-1 mt-0.5">
                  <p className={order.total_amount != null ? 'font-semibold tabular-nums' : 'text-zinc-400 italic'}>
                    {order.total_amount != null ? Number(order.total_amount).toLocaleString('fr-FR') : 'Non renseigné'}
                  </p>
                  {canWrite && (
                    <Button size="icon" variant="ghost" onClick={() => setEditingAmount(true)} title="Modifier le montant" data-testid="b2b-amount-edit">
                      <Wallet className="w-3.5 h-3.5 text-zinc-400" />
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div data-testid="b2b-paydue-block">
              <p className="text-xs text-zinc-500 uppercase">Échéance paiement</p>
              {editingPayDue && canWrite ? (
                <div className="flex items-center gap-1 mt-0.5">
                  <Input type="date" value={payDueDraft} onChange={(e) => setPayDueDraft(e.target.value)} className="h-8 text-sm" data-testid="b2b-paydue-input" />
                  <Button size="icon" variant="ghost" onClick={applyPayDue} disabled={saving} data-testid="b2b-paydue-save"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => { setEditingPayDue(false); setPayDueDraft(order.payment_due_date || ''); }}><XCircle className="w-4 h-4 text-zinc-400" /></Button>
                </div>
              ) : (
                <div className="flex items-center gap-1 mt-0.5">
                  <p className={order.payment_due_date ? '' : 'text-zinc-400 italic'}>{order.payment_due_date || 'Non définie'}</p>
                  {canWrite && (
                    <Button size="icon" variant="ghost" onClick={() => setEditingPayDue(true)} title="Modifier l'échéance" data-testid="b2b-paydue-edit">
                      <Clock className="w-3.5 h-3.5 text-zinc-400" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
          {order.comment && (
            <div className="mt-4 p-3 bg-zinc-50 rounded-lg">
              <p className="text-xs text-zinc-500 uppercase mb-1">Commentaire</p>
              <p className="text-sm">{order.comment}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pièce jointe : demande client (capture WhatsApp, photo, PDF, scan...) */}
      <Card className="border-zinc-200" data-testid="b2b-attachment-card">
        <CardContent className="p-4">
          <h3 className="text-sm font-bold text-zinc-700 mb-3 flex items-center gap-1">
            <Paperclip className="w-4 h-4 text-blue-600" /> Demande du client jointe
          </h3>
          {order.attachment_storage_path ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                {(order.attachment_mime || '').startsWith('image/') ? <ImageIcon className="w-4 h-4 text-blue-700 shrink-0" /> : <FileText className="w-4 h-4 text-blue-700 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" title={order.attachment_name}>{order.attachment_name || 'pièce jointe'}</p>
                  <p className="text-[10px] text-zinc-500">
                    {order.attachment_size ? `${Math.round(order.attachment_size / 1024)} Ko` : ''} · Joint {fmtDateTime(order.attachment_uploaded_at)} par {order.attachment_uploaded_by || '—'}
                  </p>
                </div>
                <a
                  href={`${API}/b2b/orders/${order.id}/attachment/file`}
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const r = await axios.get(`${API}/b2b/orders/${order.id}/attachment/file`, { headers: auth(), responseType: 'blob' });
                      const url = URL.createObjectURL(r.data);
                      window.open(url, '_blank', 'noopener');
                    } catch { toast.error('Impossible de récupérer la pièce'); }
                  }}
                  className="text-blue-700 hover:text-blue-900"
                  data-testid="b2b-attachment-view"
                ><Eye className="w-4 h-4" /></a>
              </div>
              {canWrite && (
                <label className="block">
                  <span className="text-[10px] text-zinc-500">Remplacer la pièce jointe :</span>
                  <input
                    type="file"
                    accept=".pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
                    disabled={saving}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(f); e.target.value = ''; }}
                    className="text-xs mt-1 block"
                    data-testid="b2b-attachment-replace"
                  />
                </label>
              )}
            </div>
          ) : canWrite ? (
            <label className="block p-4 border-2 border-dashed border-zinc-300 rounded-lg text-center hover:border-blue-400 transition cursor-pointer">
              <Paperclip className="w-6 h-6 mx-auto text-zinc-400 mb-1" />
              <p className="text-xs text-zinc-600">Joindre la demande du client (capture WhatsApp, photo, PDF — max 8 Mo)</p>
              <input
                type="file"
                accept=".pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
                disabled={saving}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(f); e.target.value = ''; }}
                className="hidden"
                data-testid="b2b-attachment-upload"
              />
            </label>
          ) : (
            <p className="text-xs text-zinc-400 italic">Aucune pièce jointe.</p>
          )}
        </CardContent>
      </Card>

      <CotationUploadCard
        order={order}
        canWrite={canWrite}
        onChanged={async () => { await reload(); onChanged?.(); }}
        onSendClick={() => setShowSend(true)}
      />

      {/* Phase B (mai 2026) : Timeline des versions de cotation */}
      <Card className="border-zinc-200">
        <CardContent className="p-5">
          <CotationVersionsTimeline
            orderId={order.id}
            canWrite={canWrite}
            canDelete={canDelete}
            onChanged={async () => { await reload(); onChanged?.(); }}
          />
        </CardContent>
      </Card>

      {/* Phase 2A.1 + 2C.1 : Workflow PDF Médiciel (parse + validation client) */}
      <Card className="border-zinc-200">
        <CardContent className="p-5">
          <B2BPdfWorkflowSection
            order={order}
            canWrite={canWrite}
            onChanged={async () => { await reload(); onChanged?.(); }}
          />
        </CardContent>
      </Card>

      {/* Phase 2D : Bons de livraison avec signature électronique */}
      <Card className="border-zinc-200">
        <CardContent className="p-5">
          <B2BDeliveryNotesSection
            order={order}
            canWrite={canWrite}
            canDelete={canDelete}
          />
        </CardContent>
      </Card>

      {/* Phase C : Documents post-livraison (Facture / Ticket / Autre) */}
      <Card className="border-zinc-200">
        <CardContent className="p-5">
          <B2BDocumentsSection order={order} canWrite={canWrite} />
        </CardContent>
      </Card>

      <Card className="border-zinc-200">
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-zinc-700 mb-3 flex items-center gap-1"><Package className="w-4 h-4" /> Lignes de la demande</h3>
          {(!order.items || order.items.length === 0) ? (
            <p className="text-sm text-zinc-400 italic">Aucune ligne renseignée.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 uppercase">
                <tr>
                  <th className="text-left py-1">Désignation</th>
                  <th className="text-left py-1">Code</th>
                  <th className="text-right py-1">Qté</th>
                  <th className="text-left py-1 pl-3">Unité</th>
                  <th className="text-left py-1 pl-3">Délai livraison</th>
                  <th className="text-left py-1 pl-3">Source</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((it, i) => {
                  const la = lineActionByIndex[i];
                  const rowClass = la
                    ? `border-t border-zinc-100 align-top ${LINE_ACTION_TONES[la.action]?.split(' ')[0] || 'bg-amber-50'} border-l-4 border-l-amber-400`
                    : 'border-t border-zinc-100 align-top';
                  return (
                    <tr key={i} className={rowClass} data-testid={la ? `b2b-item-row-with-action-${i}` : `b2b-item-row-${i}`}>
                      <td className="py-1.5">
                        <div>{it.designation}</div>
                        {la && (
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <Badge variant="outline" className={`text-[10px] ${LINE_ACTION_TONES[la.action] || ''}`}>
                              Client : {LINE_ACTION_LABELS[la.action] || la.action}
                            </Badge>
                            {la.requested_quantity != null && (
                              <span className="text-[11px] text-zinc-600">→ Qté demandée : <strong>{la.requested_quantity}</strong></span>
                            )}
                            {la.suggested_designation && (
                              <span className="text-[11px] text-zinc-600">→ Équivalent : <em>{la.suggested_designation}</em></span>
                            )}
                            {la.comment && (
                              <span className="text-[11px] text-zinc-700 italic w-full">« {la.comment} »</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-1.5 text-zinc-500">{it.code || '—'}</td>
                      <td className="py-1.5 text-right font-semibold">{it.quantity}</td>
                      <td className="py-1.5 pl-3 text-zinc-500">{it.unit || '—'}</td>
                      <td className="py-1.5 pl-3 min-w-[180px]">
                        <DeliveryDelayCell
                          orderId={order.id}
                          itemIndex={i}
                          value={it.delivery_delay || '48h après validation'}
                          canWrite={canWrite}
                          onChanged={reload}
                        />
                      </td>
                      <td className="py-1.5 pl-3"><Badge variant={it.source === 'catalog' ? 'default' : 'outline'} className="text-[10px]">{it.source === 'catalog' ? 'Catalogue' : 'Libre saisie'}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {canWrite && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="p-4">
              <h3 className="text-sm font-bold text-zinc-700 mb-2 flex items-center gap-1"><Truck className="w-4 h-4" /> Changer le statut du dossier</h3>
              <select className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="b2b-status-select">
                {meta?.statuses?.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <Textarea rows={2} className="mt-2" placeholder="Commentaire (optionnel)" value={statusComment} onChange={(e) => setStatusComment(e.target.value)} data-testid="b2b-status-comment" />
              <Button onClick={applyStatus} disabled={saving || newStatus === order.status} className="w-full mt-2 bg-amber-600 hover:bg-amber-700" data-testid="b2b-status-apply">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Appliquer le statut
              </Button>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/30">
            <CardContent className="p-4">
              <h3 className="text-sm font-bold text-zinc-700 mb-2 flex items-center gap-1"><Wallet className="w-4 h-4" /> Mettre à jour le paiement</h3>
              <select className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" value={newPay} onChange={(e) => setNewPay(e.target.value)} data-testid="b2b-payment-select">
                {meta?.payment_statuses?.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <Textarea rows={2} className="mt-2" placeholder="Commentaire (optionnel)" value={payComment} onChange={(e) => setPayComment(e.target.value)} data-testid="b2b-payment-comment" />
              <Button onClick={applyPay} disabled={saving || newPay === order.status_payment} className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700" data-testid="b2b-payment-apply">
                <Wallet className="w-4 h-4 mr-1" /> Appliquer le paiement
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="border-rose-200 bg-rose-50/30">
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-zinc-700 mb-3 flex items-center gap-1">
            <AlertCircle className="w-4 h-4 text-rose-600" /> Observations · Retards · Non-performances ({comments.length})
          </h3>
          <p className="text-xs text-zinc-500 mb-3">
            Documenter ici tout retard, blocage, non-performance ou écart de process. Chaque entrée est horodatée et signée.
          </p>

          {canWrite && (
            <div className="bg-white p-3 rounded-lg border border-zinc-200 mb-3 space-y-2" data-testid="b2b-comment-form">
              <div className="flex items-center gap-2">
                <Label className="text-xs font-medium text-zinc-600">Type</Label>
                <select
                  className="h-9 px-2 rounded-md border border-zinc-300 text-sm bg-white"
                  value={commentTag}
                  onChange={(e) => setCommentTag(e.target.value)}
                  data-testid="b2b-comment-tag"
                >
                  <option value="autre">Observation</option>
                  <option value="retard">Retard</option>
                  <option value="blocage">Blocage</option>
                  <option value="non_performance">Non-performance</option>
                  <option value="ecart_process">Écart process</option>
                </select>
              </div>
              <Textarea
                rows={2}
                placeholder="Ex: Cotation envoyée le 12/05, relance le 18/05 sans retour, blocage validation client."
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                data-testid="b2b-comment-body"
              />
              <div className="flex justify-end">
                <Button onClick={addComment} disabled={saving || !commentDraft.trim()} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="b2b-comment-submit">
                  <Save className="w-4 h-4 mr-1" /> Ajouter l&apos;observation
                </Button>
              </div>
            </div>
          )}

          {comments.length === 0 ? (
            <p className="text-sm text-zinc-400 italic">Aucune observation documentée.</p>
          ) : (
            <ul className="space-y-2">
              {comments.map((c) => (
                <li key={c.id} className="bg-white p-3 rounded-lg border border-zinc-200" data-testid={`b2b-comment-${c.id}`}>
                  <div className="flex items-center gap-2 flex-wrap text-xs mb-1">
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${c.tag === 'retard' ? 'border-amber-300 text-amber-700 bg-amber-50' : c.tag === 'blocage' ? 'border-red-300 text-red-700 bg-red-50' : c.tag === 'non_performance' ? 'border-orange-300 text-orange-700 bg-orange-50' : c.tag === 'ecart_process' ? 'border-violet-300 text-violet-700 bg-violet-50' : 'border-zinc-300 text-zinc-700 bg-zinc-50'}`}
                    >
                      {c.tag_label || 'Observation'}
                    </Badge>
                    <span className="font-semibold text-zinc-700">{fmtDateTime(c.at)}</span>
                    <span className="text-zinc-400">par {c.user_name || '—'}</span>
                  </div>
                  <p className="text-sm text-zinc-800 whitespace-pre-wrap">{c.body}</p>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="border-zinc-200">
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-zinc-700 mb-3 flex items-center gap-1"><History className="w-4 h-4" /> Historique du dossier ({history.length})</h3>
          {history.length === 0 ? (
            <p className="text-sm text-zinc-400">Aucun événement.</p>
          ) : (
            <ul className="space-y-2">
              {history.map((h) => (
                <li key={h.id} className="text-xs border-l-2 border-zinc-200 pl-3 py-1" data-testid={`b2b-history-${h.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-zinc-700">{fmtDateTime(h.at)}</span>
                    <Badge variant="outline" className="text-[10px]">{h.type === 'payment' ? 'Paiement' : 'Statut'}</Badge>
                    <span className="text-zinc-500">{h.from_value || '∅'} → <strong className="text-zinc-900">{h.to_value}</strong></span>
                    <span className="text-zinc-400">par {h.user_name || '—'}</span>
                  </div>
                  {h.comment && <p className="text-zinc-600 mt-0.5 italic">« {h.comment} »</p>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <SendCotationModal
        open={showSend}
        onClose={() => setShowSend(false)}
        order={order}
        clinics={clinics}
        onSent={async () => { await reload(); onChanged?.(); }}
      />
      <DeleteConfirmModal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        title="Supprimer ce dossier B2B ?"
        body="Le dossier sera déplacé dans la corbeille (accessible uniquement par l'Admin). Il pourra être restauré."
        onConfirm={doDelete}
      />
    </div>
  );
};
